import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #vendorSortObserver;

  #vendorSortScheduled = false;

  #collator = new Intl.Collator(document.documentElement.lang || undefined, {
    numeric: true,
    sensitivity: 'base',
  });

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    window.addEventListener('popstate', this.#scheduleVendorSort);

    this.#vendorSortObserver = new MutationObserver(this.#scheduleVendorSort);
    this.#vendorSortObserver.observe(this, { childList: true, subtree: true });

    this.#scheduleVendorSort();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    window.removeEventListener('popstate', this.#scheduleVendorSort);
    this.#vendorSortObserver?.disconnect();
  }

  #scheduleVendorSort = () => {
    if (this.#vendorSortScheduled) return;

    this.#vendorSortScheduled = true;
    queueMicrotask(() => {
      this.#vendorSortScheduled = false;
      this.#applyVendorSort();
    });
  };

  #applyVendorSort() {
    const sortValue = new URL(window.location.href).searchParams.get('sort_by');
    if (sortValue !== 'vendor-ascending' && sortValue !== 'vendor-descending') return;

    this.#syncVendorSortControls(sortValue);

    const grid = this.querySelector('[ref="grid"]');
    if (!(grid instanceof HTMLUListElement)) return;

    const cards = Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'));
    const direction = sortValue === 'vendor-descending' ? -1 : 1;
    const sortedCards = [...cards].sort((firstCard, secondCard) => {
      const firstVendor = firstCard.dataset.productVendor?.trim() ?? '';
      const secondVendor = secondCard.dataset.productVendor?.trim() ?? '';

      if (!firstVendor && secondVendor) return 1;
      if (firstVendor && !secondVendor) return -1;

      const vendorComparison = this.#collator.compare(firstVendor, secondVendor);
      if (vendorComparison !== 0) return vendorComparison * direction;

      const firstTitle = firstCard.dataset.productTitle?.trim() ?? '';
      const secondTitle = secondCard.dataset.productTitle?.trim() ?? '';
      return this.#collator.compare(firstTitle, secondTitle);
    });

    const orderChanged = cards.some((card, index) => card !== sortedCards[index]);
    if (orderChanged) grid.append(...sortedCards);
  }

  #syncVendorSortControls(sortValue) {
    for (const sortingComponent of this.querySelectorAll('sorting-filter-component')) {
      const select = sortingComponent.querySelector('select[name="sort_by"]');
      if (select instanceof HTMLSelectElement) select.value = sortValue;

      for (const option of sortingComponent.querySelectorAll('[role="option"]')) {
        const input = option.querySelector('input[name="sort_by"]');
        if (!(input instanceof HTMLInputElement)) continue;

        const isSelected = input.value === sortValue;
        input.checked = isSelected;
        option.setAttribute('aria-selected', isSelected.toString());
        option.tabIndex = isSelected ? 0 : -1;

        if (isSelected) {
          const status = sortingComponent.querySelector('.facets__status');
          const statusText = input.dataset.optionName ?? '';
          if (status && status.textContent !== statusText) status.textContent = statusText;
        }
      }
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
